/* ui-settings.js — the Settings screen: connection, history window, sync,
   classification taxonomy editor, and the danger zone. */
(function () {
  'use strict';
  const CAD = (window.CAD = window.CAD || {});

  // Editor state for the classification section.
  const cls = { valid: false, parsed: null };

  function esc(s) { return CAD.util.escapeHtml(s); }
  function icon(name, size) { return CAD.util.icon(name, size); }
  function $(id) { return document.getElementById(id); }

  // ---------- sync status panel (also refreshed live by sync events) ----------

  function syncStatusHTML() {
    const meta = CAD.storage.getSyncMeta();
    const running = meta.lastSyncStatus === 'running';
    const bytes = CAD.storage.approxStorageBytes();
    const QUOTA = 5 * 1024 * 1024; // conservative soft cap for the indicator
    const usedPct = Math.min(100, Math.round((bytes / QUOTA) * 100));
    const quotaClass = usedPct >= 90 ? 'danger' : usedPct >= 70 ? 'warn' : '';
    const statusText = {
      idle: 'Never synced',
      running: 'Syncing\u2026',
      success: 'Success',
      error: 'Failed',
    }[meta.lastSyncStatus] || meta.lastSyncStatus;

    return (
      '<dl class="status-grid" id="sync-status-panel">' +
      '  <div><dt>Last sync started</dt><dd>' + (meta.lastSyncStartedAt ? CAD.util.fmtDateTime(meta.lastSyncStartedAt) : '\u2014') + '</dd></div>' +
      '  <div><dt>Last sync completed</dt><dd>' + (meta.lastSyncCompletedAt ? CAD.util.fmtDateTime(meta.lastSyncCompletedAt) : '\u2014') + '</dd></div>' +
      '  <div><dt>Status</dt><dd><span class="sync-dot ' + esc(meta.lastSyncStatus) + '"></span>' + esc(statusText) + '</dd></div>' +
      '  <div><dt>Total files stored</dt><dd>' + CAD.util.fmtNumber(meta.totalFilesStored) + '</dd></div>' +
      '  <div><dt>Backfill complete</dt><dd>' + (meta.initialBackfillComplete ? 'Yes' : 'No \u2014 first sync will backfill') + '</dd></div>' +
      '  <div class="status-error"><dt>Last error</dt><dd>' + (meta.lastError ? esc(meta.lastError.message) : '\u2014') + '</dd></div>' +
      '  <div class="status-quota' + (quotaClass ? ' ' + quotaClass : '') + '"><dt>Local storage used</dt>' +
      '    <dd>' + CAD.util.fmtBytes(bytes) + ' of ~' + CAD.util.fmtBytes(QUOTA) +
      '      <span class="quota-track"><span class="quota-fill ' + quotaClass + '" style="width:' + usedPct + '%"></span></span>' +
      (usedPct >= 70 ? '<span class="quota-warning">' + icon('alert-triangle') + ' Approaching browser storage quota \u2014 consider clearing and re-syncing a shorter window.</span>' : '') +
      '    </dd></div>' +
      '</dl>'
    );
  }

  function refreshSyncPanel() {
    const panel = $('sync-status-panel');
    if (panel) panel.outerHTML = syncStatusHTML();
    const btn = $('sync-now-btn');
    if (btn) {
      const running = CAD.storage.getSyncMeta().lastSyncStatus === 'running';
      btn.disabled = running;
      btn.classList.toggle('syncing', running);
    }
    CAD.app.updateHeaderStatus();
  }

  // ---------- skeleton ----------

  function render(root) {
    const settings = CAD.storage.getSettings();
    const meta = CAD.storage.getSyncMeta();
    const taxonomy = CAD.storage.getClassification();

    cls.valid = false;
    cls.parsed = null;

    root.innerHTML =
      '<div class="settings-wrap">' +
      '  <h1>Settings</h1>' +

      '  <section class="card settings-section" aria-labelledby="sec-connection-h">' +
      '    <h2 id="sec-connection-h">' + icon('key-round') + ' Connection</h2>' +
      '    <div class="field">' +
      '      <label for="api-key-input">API key</label>' +
      '      <div class="key-row">' +
      '        <input type="password" id="api-key-input" class="input mono" placeholder="sk-ant-\u2026" autocomplete="off" spellcheck="false" value="' + esc(settings.apiKey || '') + '">' +
      '        <button class="icon-btn" id="key-visibility" type="button" title="Show / hide key" aria-label="Show or hide API key">' + icon('eye') + '</button>' +
      '      </div>' +
      '      <p class="field-hint" id="api-key-hint">Leave blank to use the key from <span class="mono">.env</span>. The local server proxies Anthropic (browser CORS is blocked).</p>' +
      '    </div>' +
      '    <div class="btn-row">' +
      '      <button class="btn" id="test-connection-btn" type="button">Test connection</button>' +
      '      <span id="test-result" class="test-result" aria-live="polite"></span>' +
      '    </div>' +
      '    <div class="warn-line">' + icon('alert-triangle') + '<span>Keys typed here are stored in plain text in this browser\u2019s local storage. Prefer <span class="mono">.env</span> for local demos, and revoke any key you paste into the UI.</span></div>' +
      '  </section>' +

      '  <section class="card settings-section" aria-labelledby="sec-history-h">' +
      '    <h2 id="sec-history-h">' + icon('calendar') + ' History window</h2>' +
      '    <fieldset class="field">' +
      '      <legend>How far back should the first sync go?</legend>' +
      '      <div class="radio-group">' +
      '        <label class="radio"><input type="radio" name="start-mode" value="all"' + (settings.startDateMode === 'all' ? ' checked' : '') + '> All time available</label>' +
      '        <label class="radio"><input type="radio" name="start-mode" value="preset"' + (settings.startDateMode !== 'all' && settings.startDateMode !== 'custom' ? ' checked' : '') + '> Preset' +
      '          <select id="preset-days" class="select">' +
      '            <option value="7"' + (settings.presetDays === 7 ? ' selected' : '') + '>Last 7 days</option>' +
      '            <option value="30"' + (settings.presetDays === 30 ? ' selected' : '') + '>Last 30 days</option>' +
      '            <option value="90"' + (settings.presetDays === 90 ? ' selected' : '') + '>Last 90 days</option>' +
      '          </select></label>' +
      '        <label class="radio"><input type="radio" name="start-mode" value="custom"' + (settings.startDateMode === 'custom' ? ' checked' : '') + '> Custom date' +
      '          <input type="date" id="custom-start-date" class="input" value="' + esc(settings.startDate || '') + '"></label>' +
      '      </div>' +
      '      <p class="field-hint">This controls how far back the first sync goes. After that, new files are picked up automatically \u2014 you won\u2019t need to touch this again unless you reset your data.</p>' +
      (meta.initialBackfillComplete
        ? '      <p class="inline-note">' + icon('info') + ' Backfill already completed \u2014 changing this has no retroactive effect. To re-import from a new window, use \u201CClear all local data\u201D below, then sync.</p>'
        : '') +
      '    </fieldset>' +
      '  </section>' +

      '  <section class="card settings-section" aria-labelledby="sec-sync-h">' +
      '    <h2 id="sec-sync-h">' + icon('refresh-cw') + ' Sync</h2>' +
      '    <div class="field">' +
      '      <label for="autosync-select">Auto-sync interval</label>' +
      '      <select id="autosync-select" class="select">' +
      '        <option value="0"' + (!settings.autoSyncMinutes ? ' selected' : '') + '>Off (manual only)</option>' +
      '        <option value="5"' + (settings.autoSyncMinutes === 5 ? ' selected' : '') + '>Every 5 minutes</option>' +
      '        <option value="15"' + (settings.autoSyncMinutes === 15 ? ' selected' : '') + '>Every 15 minutes</option>' +
      '        <option value="60"' + (settings.autoSyncMinutes === 60 ? ' selected' : '') + '>Every 60 minutes</option>' +
      '      </select>' +
      '    </div>' +
      '    <div class="btn-row">' +
      '      <button class="btn" id="sync-now-btn" type="button"><span class="btn-icon">' + icon('refresh-cw') + '</span> Sync now</button>' +
      '      <button class="btn btn-primary" id="save-settings-btn" type="button">Save settings</button>' +
      '    </div>' +
      syncStatusHTML() +
      '  </section>' +

      '  <section class="card settings-section" aria-labelledby="sec-classification-h">' +
      '    <h2 id="sec-classification-h">' + icon('file-question') + ' Classification</h2>' +
      '    <p class="field-hint">Keyword taxonomy applied to filenames. Save &amp; re-classify re-buckets every stored file locally \u2014 no network call.</p>' +
      '    <textarea id="class-editor" class="mono class-editor" spellcheck="false" aria-label="Classification JSON editor"></textarea>' +
      '    <div class="btn-row">' +
      '      <button class="btn" id="class-validate-btn" type="button">Validate</button>' +
      '      <button class="btn btn-primary" id="class-save-btn" type="button" disabled>Save &amp; re-classify</button>' +
      '      <span class="btn-spacer"></span>' +
      '      <button class="btn" id="class-reset-btn" type="button">Reset to default</button>' +
      '      <button class="btn" id="class-export-btn" type="button">' + icon('download') + ' Export</button>' +
      '      <button class="btn" id="class-import-btn" type="button">' + icon('upload') + ' Import</button>' +
      '      <input type="file" id="class-import-file" accept="application/json,.json" hidden>' +
      '    </div>' +
      '    <div id="class-validate-result" class="validation-result" aria-live="polite"></div>' +
      '  </section>' +

      '  <section class="card settings-section danger-section" aria-labelledby="sec-danger-h">' +
      '    <h2 id="sec-danger-h">' + icon('trash-2') + ' Danger zone</h2>' +
      '    <p>Clears the API key, sync history and watermark, all ' + CAD.util.fmtNumber(CAD.storage.getFileCount()) + ' cached file records, and any custom taxonomy. Default taxonomy is restored.</p>' +
      '    <button class="btn btn-danger" id="clear-data-btn" type="button">Clear all local data</button>' +
      '  </section>' +
      '</div>';

    const editor = $('class-editor');
    editor.value = taxonomy ? JSON.stringify(taxonomy, null, 2) : '';
    editor.addEventListener('input', invalidateClassification);

    wire();
    refreshSyncPanel();

    CAD.api.loadConfig().then(function (cfg) {
      const hint = $('api-key-hint');
      if (!hint) return;
      if (cfg.hasEnvKey) {
        hint.innerHTML = 'A key is loaded from <span class="mono">.env</span> \u2014 leave this blank to use it. The local server proxies Anthropic (browser CORS is blocked).';
      } else {
        hint.innerHTML = 'Paste a key here, or add <span class="mono">ANTHROPIC_API_KEY</span> / <span class="mono">claude_key</span> to <span class="mono">.env</span>. The local server proxies Anthropic.';
      }
    });
  }

  function invalidateClassification() {
    cls.valid = false;
    cls.parsed = null;
    const save = $('class-save-btn');
    if (save) save.disabled = true;
    const out = $('class-validate-result');
    if (out) out.innerHTML = '<span class="muted">Edited \u2014 run Validate before saving.</span>';
  }

  // ---------- classification section handlers ----------

  function validateClassification(fromSave) {
    const editor = $('class-editor');
    const out = $('class-validate-result');
    const save = $('class-save-btn');
    let parsed;
    try {
      parsed = JSON.parse(editor.value);
    } catch (err) {
      cls.valid = false; cls.parsed = null;
      save.disabled = true;
      out.innerHTML = '<span class="err">' + icon('alert-triangle') + ' Invalid JSON: ' + esc(err.message) + '</span>';
      return false;
    }
    const result = CAD.classify.validateTaxonomy(parsed);
    if (!result.ok) {
      cls.valid = false; cls.parsed = null;
      save.disabled = true;
      out.innerHTML = '<span class="err">' + icon('alert-triangle') + ' Invalid taxonomy:</span><ul class="err-list">' +
        result.errors.map(function (e) { return '<li>' + esc(e) + '</li>'; }).join('') + '</ul>';
      return false;
    }
    cls.valid = true;
    cls.parsed = parsed;
    const kw = parsed.categories.reduce(function (n, c) { return n + (Array.isArray(c.keywords) ? c.keywords.length : 0); }, 0);
    out.innerHTML = '<span class="ok">' + icon('check') + ' Valid \u2014 ' + parsed.categories.length + ' categories, ' + kw + ' keywords.</span>';
    if (!fromSave) save.disabled = false;
    return true;
  }

  function saveClassification() {
    if (!validateClassification(true)) return;
    CAD.storage.saveClassification(cls.parsed);
    const n = CAD.classify.reclassifyAll(cls.parsed);
    CAD.util.toast('Saved \u2014 re-classified ' + CAD.util.fmtNumber(n) + ' stored files (no network calls).');
    $('class-save-btn').disabled = false;
    CAD.app.refresh();
  }

  // ---------- wiring ----------

  function wire() {
    // Connection
    $('key-visibility').addEventListener('click', function () {
      const input = $('api-key-input');
      const showing = input.type === 'text';
      input.type = showing ? 'password' : 'text';
      this.innerHTML = icon(showing ? 'eye' : 'eye-off');
      this.setAttribute('aria-label', showing ? 'Show API key' : 'Hide API key');
    });

    $('test-connection-btn').addEventListener('click', async function () {
      const key = $('api-key-input').value.trim();
      const out = $('test-result');
      this.disabled = true;
      out.innerHTML = '<span class="muted">Testing\u2026</span>';
      try {
        if (!(await CAD.api.hasCredential(key))) {
          out.innerHTML = '<span class="err">Enter a key or set one in .env.</span>';
          return;
        }
        await CAD.api.testConnection(key);
        out.innerHTML = '<span class="ok">' + icon('check') + ' Connected' + (key ? '' : ' (via .env)') + '</span>';
      } catch (err) {
        out.innerHTML = '<span class="err">' + icon('alert-triangle') + ' ' + esc(CAD.api.describeError(err)) + '</span>';
      } finally {
        this.disabled = false;
      }
    });

    // Save settings (connection + history + sync)
    $('save-settings-btn').addEventListener('click', function () {
      const mode = (document.querySelector('input[name="start-mode"]:checked') || {}).value || 'preset';
      const custom = $('custom-start-date').value;
      if (mode === 'custom' && !custom) {
        CAD.util.toast('Pick a custom start date, or choose another history window.', 'err');
        return;
      }
      CAD.storage.saveSettings({
        apiKey: $('api-key-input').value.trim(),
        startDateMode: mode,
        presetDays: parseInt($('preset-days').value, 10) || 30,
        startDate: mode === 'custom' ? custom : null,
        autoSyncMinutes: parseInt($('autosync-select').value, 10) || 0,
      });
      CAD.app.armAutoSync();
      CAD.app.updateHeaderStatus();
      CAD.util.toast('Settings saved.');
      const note = rootNote();
      if (note) note.remove();
      // Re-render so the history-window inline note appears/disappears correctly.
      CAD.settings.render(document.getElementById('view-settings'));
    });

    // Sync now
    $('sync-now-btn').addEventListener('click', function () {
      CAD.sync.runSync({ manual: true });
      refreshSyncPanel();
    });

    // Classification
    $('class-validate-btn').addEventListener('click', function () { validateClassification(false); });
    $('class-save-btn').addEventListener('click', saveClassification);
    $('class-reset-btn').addEventListener('click', async function () {
      const def = await CAD.storage.loadDefaultClassification();
      $('class-editor').value = JSON.stringify(def, null, 2);
      invalidateClassification();
      CAD.util.toast('Default taxonomy loaded into the editor \u2014 Validate, then Save.');
    });
    $('class-export-btn').addEventListener('click', function () {
      let text = $('class-editor').value;
      try { JSON.parse(text); } catch (err) {
        const saved = CAD.storage.getClassification();
        text = JSON.stringify(saved, null, 2);
        CAD.util.toast('Editor content was invalid JSON \u2014 exported the last saved taxonomy instead.', 'err');
      }
      CAD.util.downloadText('classification.json', text, 'application/json');
    });
    $('class-import-btn').addEventListener('click', function () { $('class-import-file').click(); });
    $('class-import-file').addEventListener('change', function () {
      const file = this.files && this.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = function () {
        $('class-editor').value = String(reader.result);
        invalidateClassification();
        CAD.util.toast('Imported \u201C' + file.name + '\u201D into the editor. Validate, then Save.');
      };
      reader.readAsText(file);
      this.value = '';
    });

    // Danger zone
    $('clear-data-btn').addEventListener('click', confirmClearAll);
  }

  function rootNote() { return document.querySelector('#view-settings .save-toast-note'); }

  function confirmClearAll() {
    const n = CAD.storage.getFileCount();
    CAD.ui.modal({
      title: 'Clear all local data?',
      className: 'confirm-modal',
      content:
        '<div class="confirm-body">' +
        '  <p>This permanently deletes from this browser:</p>' +
        '  <ul>' +
        '    <li>Your stored API key</li>' +
        '    <li>Sync history and the incremental watermark</li>' +
        '    <li>All ' + CAD.util.fmtNumber(n) + ' cached file records</li>' +
        '    <li>Your custom classification taxonomy (default restored)</li>' +
        '  </ul>' +
        '  <p class="muted">Nothing is deleted from your Anthropic account \u2014 only this browser\u2019s copy.</p>' +
        '</div>' +
        '<div class="confirm-actions">' +
        '  <button class="btn" data-confirm="cancel" type="button">Cancel</button>' +
        '  <button class="btn btn-danger" data-confirm="go" type="button">Clear everything</button>' +
        '</div>',
      onOpen: function (m) {
        m.el.querySelector('[data-confirm="cancel"]').addEventListener('click', m.close);
        m.el.querySelector('[data-confirm="go"]').addEventListener('click', async function () {
          CAD.storage.clearAll();
          await CAD.storage.ensureClassification();
          CAD.util.toast('All local data cleared. Default taxonomy restored.');
          m.close();
          CAD.app.resetDashboardState();
          CAD.app.showView('dashboard');
          CAD.app.updateHeaderStatus();
        });
      },
    });
  }

  CAD.settings = {
    render: render,
    refreshSyncPanel: refreshSyncPanel,
  };
})();
